import '../professions/professions.css'
import { LocationWindow } from '../LocationWindow'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { Board, BoardOffer, BoardStep, Evidence, StoryQuest, World } from '@mezha/api'
import { useBoardCommandMutation, useBoardQuery, useEvidenceQuery, useStoryClaimMutation, useStoryQuery } from '../../api/endpoints/quests'
import { useWorldQuery } from '../../api/endpoints/world'
import { useCommand } from '../../lib/useCommand'
import { clockText, countdown, useServerNow } from '../../lib/time'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { ActionButton, RowAction, cx } from '../../ui'
import { RewardRow, rewardText } from '../common'
import { SagaTab } from './Saga'
import { GoalsTab } from './Goals'
import { chapterTitle, publishedStoryComplete } from './progression'
import './journal.css'
import { currentStoryQuest, prioritizeStoryQuests } from '../questNavigation'

const TABS = [{ id: 'story', label: 'Путь' }, { id: 'goals', label: 'Дела и рост' }, { id: 'board', label: 'Поручения' }, { id: 'evidence', label: 'Улики' }, { id: 'saga', label: 'Повесть' }]
type Tab = 'story' | 'goals' | 'board' | 'evidence' | 'saga'

/** `/play/journal`: the story path, the board of errands, the evidence and the novella opened by rewards. */
export function Journal() {
  useShellMode('window')
  const [params, setParams] = useSearchParams()
  const tab = (TABS.some(t => t.id === params.get('tab')) ? params.get('tab') : 'story') as Tab
  return (
    <LocationWindow title="Журнал" emblem="nav.journal" tabs={TABS} activeTab={tab} onTab={t => setParams(t === 'story' ? {} : { tab: t }, { replace: true })}
      idBase="journal" bodyClassName="journal">
      {tab === 'story' && <StoryTab />}
      {tab === 'goals' && <GoalsTab />}
      {tab === 'board' && <BoardTab />}
      {tab === 'evidence' && <EvidenceTab />}
      {tab === 'saga' && <SagaTab />}
    </LocationWindow>
  )
}

/** Where the current step is done: the hero's place → location, else the map. */
function goTo(world: World | undefined, placeId: string): string {
  if (world?.place_id === placeId) return '/play'
  for (const region of world ? [world.region, ...world.nearby] : []) {
    for (const cell of region.cells) {
      const place = cell.places.find(p => p.id === placeId)
      if (place) return `/play/map?${new URLSearchParams({ destination: place.name, place: placeId, x: String(cell.x), y: String(cell.y) })}`
    }
  }
  return '/play/map'
}

function StoryTab() {
  const story = useStoryQuery()
  const world = useWorldQuery()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [claim] = useStoryClaimMutation()
  const cmd = useCommand(() => void story.refetch())
  const quests = useMemo(() => prioritizeStoryQuests(story.data?.quests ?? []), [story.data])
  const [picked, setPicked] = useState<string | null>(null)
  const current = quests.find(q => q.id === picked) ?? currentStoryQuest(story.data) ?? quests[quests.length - 1] ?? null
  useEffect(() => { if (picked && !quests.some(q => q.id === picked)) setPicked(null) }, [picked, quests])

  // Each record carries its own action: «Забрать» a finished one, «В путь» to the active step.
  const onClaim = async (q: StoryQuest) => {
    const r = await cmd.run(() => claim({ quest_id: q.id }).unwrap())
    if (r) {
      setPicked(q.id)
      dispatch(ui.actions.notice(`Награда получена: ${q.title}`))
    }
  }
  const actionOf = (q: StoryQuest, big = false) => {
    if (q.status === 'completed') return <RowAction gold big={big} emblem="res.silver" onPress={() => onClaim(q)} aria-label={`Забрать награду: ${q.title}`}>{big ? 'Забрать награду' : 'Забрать'}</RowAction>
    const step = q.current
    if (q.status === 'active' && step) {
      const here = world.data?.place_id === step.place_id
      return <RowAction big={big} emblem="map.travel" onPress={() => navigate(goTo(world.data, step.place_id))} aria-label={`${here ? 'Вы на месте' : 'В путь'}: ${step.place_name}`}>{here ? 'На месте ▸' : 'В путь'}</RowAction>
    }
    return null
  }

  if (story.isLoading) return <Loading />
  if (story.isError && !story.data) return <Failure error={story.error} retry={() => void story.refetch()} what="Путь" />
  if (quests.length === 0) return <Empty>Записей пока нет. Первое задание откроется, когда вы оглядитесь на Меже.</Empty>

  const chapters = [...new Set(quests.map(q => q.chapter))]
  return (
    <>
      <nav className="road" aria-label="Задания">
        <Link className="profession-entry" to="/play/journal?tab=goals">Дела и рост — чем заняться →</Link>
        {chapters.map(ch => (
          <div key={ch}>
            <div className="road__act"><div className="plank"><div className="plank__in"><Emblem name="reward.saga" size={16} />{chapterTitle(ch)}</div></div></div>
            <ol className="road__list">
              {quests.filter(q => q.chapter === ch).map(q => (
                <li key={q.id} className="rline">
                  <button type="button" className={cx('rnode', q.status === 'claimed' && 'is-done', q.id === current?.id && 'is-current')} aria-current={q.id === current?.id ? 'true' : undefined}
                    onClick={() => setPicked(q.id)}>
                    <span className="rnode__mark" aria-hidden="true" />
                    <span>{q.title}<small>{q.status === 'claimed' ? 'выполнено' : q.status === 'completed' ? 'награда ждёт' : `шаг ${q.step_index + 1} из ${q.step_count}`}</small></span>
                  </button>
                  {actionOf(q)}
                </li>
              ))}
            </ol>
          </div>
        ))}
        <ol className="road__list"><li className="rnode is-locked"><span className="rnode__mark" aria-hidden="true" /><span>{story.data && publishedStoryComplete(story.data) ? 'Опубликованная история пройдена' : 'Следующая запись ещё не открыта'}</span></li></ol>
      </nav>
      {current && <QuestPage quest={current} world={world.data} error={cmd.text} action={actionOf(current, true)} />}
      <Notes />
    </>
  )
}

function QuestPage({ quest: q, world, error, action }: { quest: StoryQuest; world: World | undefined; error: string | null; action: ReactNode }) {
  return (
    <article className="qpage parch" aria-labelledby="qpage-title">
      <div className="qpage__code">{chapterTitle(q.chapter)} · шаг {Math.min(q.step_index + 1, q.step_count)} из {q.step_count}</div>
      <h2 id="qpage-title">{q.title}</h2>
      {q.summary && <p className="qpage__intro">{q.summary}</p>}
      <ol className="qsteps">
        {q.done.map(d => (
          <li key={d.id} className="qstep is-done">
            <span className="qstep__seal" aria-hidden="true" />
            <div><b>{d.title}</b><span>{d.choice_result || 'записано'}</span></div>
          </li>
        ))}
        {q.current && (
          <li className="qstep is-active" aria-current="step">
            <span className="qstep__seal" aria-hidden="true" />
            <div><b>{q.current.title}</b><span>{q.current.place_name}{world && q.current.region_id !== world.region.id ? ` · ${q.current.region_name}` : ''}{q.current.object_name ? ` · ${q.current.object_name}` : ''}</span>{q.current.text && <span>{q.current.text}</span>}
              {q.status === 'active' && <div className="qstep__act">{action}</div>}</div>
          </li>
        )}
        {q.step_index + (q.current ? 1 : 0) < q.step_count && q.status === 'active' && (
          <li className="qstep"><span className="qstep__seal" aria-hidden="true" /><div><span>Дальше — после этого шага</span></div></li>
        )}
      </ol>
      {q.status === 'claimed' && <p className="stamp">Награда получена</p>}
      {q.reward && (
        <div className="qreward">Награда:<div>{rewardText(q.reward)}</div>{q.status === 'completed' && action}</div>
      )}
      {error && <p className="journal__error" role="alert">{error}</p>}
    </article>
  )
}

function Notes() {
  const ev = useEvidenceQuery()
  return (
    <aside className="notes" aria-label="Записи и улики">
      <div className="eyebrow">Записи и улики</div>
      {ev.isLoading && <Loading />}
      {ev.isError && !ev.data && <Failure error={ev.error} retry={() => void ev.refetch()} what="Улики" />}
      {ev.data?.items.length === 0 && <p className="note">Пока ничего не записано.</p>}
      {ev.data?.items.slice(0, 6).map(e => <EvidenceCard key={e.id} e={e} />)}
    </aside>
  )
}

const EVIDENCE_RU: Record<string, string> = { item: 'предмет', testimony: 'показания', observation: 'наблюдение' }

function EvidenceCard({ e }: { e: Evidence }) {
  return (
    <div className="note-card slab">
      <Emblem name={e.kind === 'testimony' ? 'journal.testimony' : e.kind === 'item' ? 'journal.item' : 'journal.observation'} />
      <div>{e.text}<small>{EVIDENCE_RU[e.kind] ?? e.kind}{e.item_id ? ` · предмет в суме` : ''}</small></div>
    </div>
  )
}

function EvidenceTab() {
  const ev = useEvidenceQuery()
  if (ev.isLoading) return <Loading />
  if (ev.isError && !ev.data) return <Failure error={ev.error} retry={() => void ev.refetch()} what="Улики" />
  if (!ev.data?.items.length) return <Empty>Улик пока нет: их дают осмотры, разговоры и находки по сюжету.</Empty>
  return <div className="evidence">{ev.data.items.map(e => <EvidenceCard key={e.id} e={e} />)}</div>
}

const OFFER_STATUS: Record<string, string> = { offered: 'предложено', active: 'взято', ready_to_claim: 'готово', claimed: 'закрыто', declined: 'отклонено', abandoned: 'брошено', expired: 'истекло' }

function stepText(s: BoardStep): string {
  const target = s.target_name || s.place_name
  const count = s.count > 1 ? ` · ${s.progress}/${s.count}` : ''
  return `${s.text}${target && !s.text.includes(target) ? ` (${target})` : ''}${count}`
}

function BoardTab() {
  const board = useBoardQuery()
  const now = useServerNow(1000)
  const [command] = useBoardCommandMutation()
  const cmd = useCommand(() => void board.refetch())
  const [picked, setPicked] = useState<string | null>(null)
  const b = board.data
  const all = useMemo(() => (b ? [...b.taken, ...b.offers] : []), [b])
  const sel = all.find(o => o.id === picked) ?? all[0] ?? null
  const run = (o: BoardOffer, action: 'accept' | 'decline' | 'abandon' | 'claim') => cmd.run(() => command({ offer_id: o.id, action }).unwrap())

  const world = useWorldQuery()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const DONE: Partial<Record<'accept' | 'decline' | 'abandon' | 'claim', string>> = { accept: 'Поручение взято', claim: 'Награда получена', decline: 'Поручение отклонено', abandon: 'Поручение брошено' }
  const act = async (o: BoardOffer, a: 'accept' | 'decline' | 'abandon' | 'claim') => {
    const r = await run(o, a)
    if (r) {
      setPicked(o.id)
      dispatch(ui.actions.notice(`${DONE[a]}: ${o.title}`))
    }
  }
  const full = b ? b.taken.filter(x => x.status === 'active' || x.status === 'ready_to_claim').length >= b.max_active : false
  const offerAction = (o: BoardOffer, big = false) => {
    if (o.status === 'offered') return <RowAction gold big={big} emblem="loc.task" locked={full} onPress={() => act(o, 'accept')} aria-label={`Взять: ${o.title}`}>Взять</RowAction>
    if (o.status === 'ready_to_claim') return <RowAction gold big={big} emblem="res.silver" onPress={() => act(o, 'claim')} aria-label={`Забрать награду: ${o.title}`}>{big ? 'Забрать награду' : 'Забрать'}</RowAction>
    const step = o.status === 'active' ? o.steps.find(s => !s.done) : undefined
    if (step?.place_id) {
      const place = step.place_id
      return <RowAction big={big} emblem="map.travel" onPress={() => navigate(goTo(world.data, place))} aria-label={`В путь: ${o.title}`}>В путь</RowAction>
    }
    return null
  }

  if (board.isLoading) return <Loading />
  if (board.isError && !b) {
    const locked = (board.error as { code?: string } | undefined)?.code === 'board_locked'
    return locked ? <Empty>Доска поручений откроется позже — по ходу сюжета.</Empty> : <Failure error={board.error} retry={() => void board.refetch()} what="Поручения" />
  }
  if (!b) return null
  if (b.locked) return <Empty>Доска поручений закрыта. Она откроется по ходу сюжета.</Empty>
  const taken = b.taken.filter(o => o.status === 'active' || o.status === 'ready_to_claim').length
  const rotation = countdown(now, b.rotation_ends_at)

  return (
    <>
      <nav className="road" aria-label="Поручения">
        <Link className="profession-entry" to="/play/professions">Мирные мастерства и заказы жителей →</Link>
        <div className="eyebrow road__sep">Взято {taken} из {b.max_active} · смена доски через {rotation.text}</div>
        <ol className="road__list">
          {all.length === 0 && <li className="rnode is-locked"><span className="rnode__mark" aria-hidden="true" /><span>Новых поручений нет</span></li>}
          {all.map(o => (
            <li key={o.id} className="rline">
              <button type="button" className={cx('rnode', o.status === 'claimed' && 'is-done', sel?.id === o.id && 'is-current')} onClick={() => setPicked(o.id)} aria-current={sel?.id === o.id ? 'true' : undefined}>
                <span className="rnode__mark" aria-hidden="true" />
                <span>{o.title}<small>{o.resident_name ? `Заказ: ${o.resident_name} · ` : ''}{OFFER_STATUS[o.status] ?? o.status}{o.status === 'offered' || o.status === 'active' ? ` · до ${clockText(o.expires_at)}` : ''}</small></span>
              </button>
              {offerAction(o)}
            </li>
          ))}
        </ol>
      </nav>
      {sel ? <OfferPage offer={sel} board={b} onCommand={a => act(sel, a)} action={offerAction(sel, true)} error={cmd.text} /> : <Empty>Выберите поручение.</Empty>}
    </>
  )
}

type OfferPageProps = { offer: BoardOffer; board: Board; onCommand: (a: 'accept' | 'decline' | 'abandon' | 'claim') => Promise<unknown>; action: ReactNode; error: string | null }

function OfferPage({ offer: o, board, onCommand, action, error }: OfferPageProps) {
  const full = board.taken.filter(x => x.status === 'active' || x.status === 'ready_to_claim').length >= board.max_active
  return (
    <article className="qpage parch" aria-labelledby="offer-title">
      <div className="qpage__code">Поручение · {OFFER_STATUS[o.status] ?? o.status}{o.combat ? ' · с боем' : ''}</div>
      <h2 id="offer-title">{o.title}</h2>
      {o.resident_name && <div className="resident-order">{o.resident_portrait && <img src={o.resident_portrait} alt="" />}<span>Просьба жителя<br /><b>{o.resident_name}</b>{(o.profession_level ?? 1) > 1 && <><br />{o.profession_name} · мастерство {o.profession_level}</>}</span></div>}
      <p className="qpage__intro">{o.description}</p>
      <ol className="qsteps">
        {o.steps.map(s => (
          <li key={s.id} className={cx('qstep', s.done && 'is-done', !s.done && s.current && 'is-active')}>
            <span className="qstep__seal" aria-hidden="true" />
            <div>
              <b>{stepText(s)}</b>
              {s.alternatives?.map(a => <span key={a.id}>или: {stepText(a)}{a.done ? ' — сделано' : ''}</span>)}
            </div>
          </li>
        ))}
      </ol>
      {o.status === 'ready_to_claim' && o.completion && <p className="stamp">{o.completion}</p>}
      <div className="qreward">Награда:<div>{rewardText(o.reward)}</div>{!!o.profession_xp && <Link to="/play/professions">+{o.profession_xp} опыта · {o.profession_name}</Link>}</div>
      {o.status === 'claimed' && <RewardRow reward={o.reward} />}
      {error && <p className="journal__error" role="alert">{error}</p>}
      <div className="actions">
        {action}
        {o.status === 'offered' && full && <span className="note">Взято {board.max_active} из {board.max_active}</span>}
        {o.status === 'offered' && <ActionButton onPress={() => onCommand('decline')}>Отказаться</ActionButton>}
        {o.status === 'active' && <ActionButton onPress={() => onCommand('abandon')}>Бросить</ActionButton>}
      </div>
    </article>
  )
}
